/**
 * CapacitorBleTransport — Bitchat-compatible BLE transport for native
 * iOS/Android builds via @capacitor-community/bluetooth-le.
 *
 * Same role as WebBluetoothTransport (central-only / observer): connects to
 * a Bitchat peer that's already advertising, subscribes to notifications,
 * decodes the inbound stream and dispatches packets to MeshClient.
 *
 * Why this exists alongside WebBluetoothTransport:
 *   - Web Bluetooth doesn't work on iOS Safari at all (Apple blocks the API).
 *   - Capacitor wraps the native CoreBluetooth / android.bluetooth APIs so
 *     a BisaWofa native build CAN scan + connect on iOS phones.
 *   - Background BLE: Capacitor can keep BLE notifications flowing when the
 *     app is backgrounded (with appropriate Info.plist entries); Web
 *     Bluetooth cannot.
 *   - Connection stability is generally better on native than over the
 *     Chromium Web Bluetooth bridge.
 *
 * NOT YET implemented (Stage 3b work):
 *   - Peripheral mode (CBPeripheralManager). The off-the-shelf plugin is
 *     central-only. Full peer-to-peer requires a custom Capacitor plugin
 *     wrapping CBPeripheralManager (iOS) and BluetoothGattServer (Android).
 *
 * Capabilities: canScan=true, canAdvertise=false, canRelay=false.
 */

import { BleTransport } from './BleTransport.js';
import { NotificationStreamAssembler } from '../NotificationStreamAssembler.js';
import { FragmentReassembler } from '../Fragmentation.js';
import { decode as decodePacket, TYPE } from '../MeshPacket.js';
import { decode as decodeAnnounce } from '../AnnouncePacket.js';

const SERVICE_UUID_MAINNET = 'f47b5e2d-4a9e-4c5a-9b3f-8e1d2c3a4b5c';
const SERVICE_UUID_TESTNET = 'f47b5e2d-4a9e-4c5a-9b3f-8e1d2c3a4b5a';
const CHARACTERISTIC_UUID  = 'a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d';

export class CapacitorBleTransport extends BleTransport {
  constructor(opts) {
    super(opts);
    this.deviceId = null;
    this.deviceName = null;
    this.serviceUuid = null;        // resolved after connect (mainnet vs testnet)
    this.assembler = new NotificationStreamAssembler((bytes) => this._onFrame(bytes));
    this.reassembler = new FragmentReassembler();
    this.gatewayPeerId = null;
    this._BleClient = null;         // lazy-loaded via dynamic import
    this._initialized = false;
  }

  get capabilities() {
    return { canScan: true, canAdvertise: false, canRelay: false, name: 'Capacitor BLE (observer)' };
  }

  /**
   * The Capacitor JS bridge is only present in a native shell. The web
   * build of BisaWofa never instantiates this transport.
   */
  static async isSupported() {
    try {
      const { Capacitor } = await import('@capacitor/core');
      return Capacitor.isNativePlatform();
    } catch (_) {
      return false;
    }
  }

  async start() {
    if (this.started) return;
    const { BleClient } = await import('@capacitor-community/bluetooth-le');
    this._BleClient = BleClient;

    // BleClient.initialize() requests OS permission. On iOS it triggers the
    // Bluetooth permission prompt; on Android it triggers fine-location +
    // BLUETOOTH_SCAN/BLUETOOTH_CONNECT permissions.
    await this._BleClient.initialize({ androidNeverForLocation: true });
    this._initialized = true;
    this.started = true;
    console.log('[CapacitorBLE] Initialized — click "Scan BLE" in the Nearby panel');
  }

  async stop() {
    if (!this.started) return;
    await this._disconnect();
    this.started = false;
    console.log('[CapacitorBLE] Stopped');
  }

  async send(packetBytes, _recipientId) {
    if (!this.deviceId || !this.serviceUuid || !this._BleClient) return;
    try {
      const dv = new DataView(packetBytes.buffer, packetBytes.byteOffset, packetBytes.byteLength);
      await this._BleClient.writeWithoutResponse(
        this.deviceId, this.serviceUuid, CHARACTERISTIC_UUID, dv,
      );
    } catch (err) {
      console.warn('[CapacitorBLE] Write failed:', err.message || err);
    }
  }

  /**
   * Pop the native BLE chooser, let the user pick a Bitchat peripheral,
   * connect, subscribe to notifications. Must be invoked from inside a
   * user gesture (button click).
   */
  async requestScan() {
    if (!this.started) await this.start();
    if (!this._BleClient) throw new Error('Capacitor BLE plugin not available');

    await this._disconnect();

    // Filter for the Bitchat service. Both mainnet and testnet UUIDs are
    // accepted (matches the App Store and debug builds respectively).
    const device = await this._BleClient.requestDevice({
      services: [SERVICE_UUID_MAINNET, SERVICE_UUID_TESTNET],
    });

    this.deviceId = device.deviceId;
    this.deviceName = device.name || device.deviceId;
    console.log('[CapacitorBLE] Connecting to', this.deviceName, this.deviceId);

    await this._BleClient.connect(this.deviceId, () => this._onUnexpectedDisconnect());

    // We don't know up front whether the peer is mainnet or testnet —
    // try mainnet first, fall back to testnet.
    this.serviceUuid = SERVICE_UUID_MAINNET;
    try {
      await this._BleClient.startNotifications(
        this.deviceId, this.serviceUuid, CHARACTERISTIC_UUID,
        (value) => this._onNotification(value),
      );
    } catch (err) {
      // Maybe a debug-build peer; retry with the testnet UUID.
      this.serviceUuid = SERVICE_UUID_TESTNET;
      await this._BleClient.startNotifications(
        this.deviceId, this.serviceUuid, CHARACTERISTIC_UUID,
        (value) => this._onNotification(value),
      );
    }

    console.log('[CapacitorBLE] Subscribed — waiting for announce…');
    return { deviceName: this.deviceName };
  }

  isConnected() {
    return !!this.deviceId;
  }

  // ---------- internals ----------

  async _disconnect() {
    if (!this.deviceId || !this._BleClient) return;
    try {
      if (this.serviceUuid) {
        await this._BleClient.stopNotifications(this.deviceId, this.serviceUuid, CHARACTERISTIC_UUID);
      }
    } catch (_) {}
    try { await this._BleClient.disconnect(this.deviceId); } catch (_) {}
    this._cleanupConnectionState();
  }

  _onUnexpectedDisconnect() {
    console.log('[CapacitorBLE] Peripheral disconnected');
    this._cleanupConnectionState();
  }

  _cleanupConnectionState() {
    if (this.gatewayPeerId) {
      const lost = this.gatewayPeerId;
      this.gatewayPeerId = null;
      try { this.handlers.onPeerLost(lost); } catch (_) {}
    }
    this.deviceId = null;
    this.deviceName = null;
    this.serviceUuid = null;
    this.assembler.reset();
  }

  _onNotification(dataView) {
    if (!dataView) return;
    const bytes = new Uint8Array(dataView.buffer, dataView.byteOffset, dataView.byteLength);
    this.assembler.feed(bytes);
  }

  _onFrame(bytes) {
    let pkt;
    try { pkt = decodePacket(bytes); }
    catch (err) {
      console.warn('[CapacitorBLE] Failed to decode packet:', err.message);
      return;
    }

    if (pkt.type === TYPE.FRAGMENT) {
      const done = this.reassembler.ingest(pkt.senderId, pkt.payload);
      if (!done) return;
      this._onFrame(done.bytes);
      return;
    }

    if (pkt.type === TYPE.ANNOUNCE) {
      const announce = decodeAnnounce(pkt.payload);
      const nickname = announce.nickname || 'unknown';
      const isGateway = !this.gatewayPeerId;
      if (isGateway) this.gatewayPeerId = pkt.senderId;
      this.handlers.onPeerSeen({
        id: pkt.senderId,
        idHex: _hex(pkt.senderId),
        nickname,
        lastSeen: Date.now(),
        rssi: 0,
        transport: isGateway ? 'capacitor-ble' : 'capacitor-ble (relayed)',
      });
    }

    this.handlers.onPacket(bytes, pkt.senderId);
  }
}

function _hex(u8) {
  return Array.from(u8, b => b.toString(16).padStart(2, '0')).join('');
}

export default CapacitorBleTransport;
